import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Plus, Search, X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { parsePesoToCentavos, centavosToPesoInput } from "@/lib/money";
import { APPROVED_PROCEDURE_CATEGORIES } from "@/lib/procedureCategories";

export default function AddRowControls({
  patientCurrency,
  onAdd,
  demoProcedures = null,
  // Emphasis mode: used when the doctor charted nothing and front desk is
  // encoding the whole visit from paper/verbal. Renders a headline + primary
  // buttons so it reads as the main action, not an afterthought.
  emphasis = false,
  heading = null,
  subheading = null,
  customLabel = "Add custom charge",
  // Bare: drop the card chrome so this can nest inside another card (e.g. as
  // the header actions of the Added-chairside table). Large: bigger buttons.
  // Split: the two buttons each fill 50% of the row, centered. Colored: teal
  // actionable styling (implied by emphasis).
  bare = false,
  large = false,
  split = false,
  colored = false,
  showOrthoChargePrompt = false,
}) {
  const bigButtons = emphasis || large;
  const useColor = emphasis || colored;
  const sizeCls = bigButtons ? "px-4 py-2 text-sm rounded-lg" : "px-3 py-1.5 text-xs rounded";
  const addCls = useColor
    ? "text-white bg-teal-600 hover:bg-teal-700"
    : "text-slate-700 bg-slate-100 hover:bg-slate-200";
  const customCls = useColor
    ? "text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200"
    : "text-slate-700 bg-slate-100 hover:bg-slate-200";
  const rowCls = split ? "grid grid-cols-2 gap-2" : "flex items-center gap-2";
  const btnWidthCls = split ? "w-full justify-center" : "";
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch]   = useState("");
  const [results, setResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [customOpen, setCustomOpen]   = useState(false);
  const [customName, setCustomName]   = useState("");
  const [customPrice, setCustomPrice] = useState(0);
  const [customCategory, setCustomCategory] = useState("");

  // search procedures
  useEffect(() => {
    if (!searchOpen) return;
    if (Array.isArray(demoProcedures)) {
      setResults(demoProcedures);
      setLoadingSearch(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingSearch(true);
      const { data } = await supabase.rpc("list_clinic_procedures");
      setLoadingSearch(false);
      if (!cancelled) setResults(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [demoProcedures, searchOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return results.slice(0, 25);
    return results.filter((r) =>
      (r.name || "").toLowerCase().includes(q)
      || (r.code || "").toLowerCase().includes(q)
    ).slice(0, 25);
  }, [results, search]);

  function pickProcedure(p) {
    const defaultFee = p.default_fee_centavos == null ? 0 : p.default_fee_centavos;
    onAdd({
      _localId:                makeLocalId(),
      procedure_id:            p.id,
      procedure_code:          p.code,
      procedure_name:          p.name,
      default_fee_centavos:    p.default_fee_centavos,
      performed_price_centavos: defaultFee,
      item_discount_centavos:  0,
      qty:                     1,
      tooth_number:            "",
      outcome:                 "done",
      _tutorialAddedProcedure:  !!p._tutorialAddedProcedure,
    });
    setSearchOpen(false);
    setSearch("");
  }

  function addCustom() {
    if (!customName.trim() || !customCategory) return;
    onAdd({
      _localId:                 makeLocalId(),
      procedure_id:             null,
      custom_name:              customName.trim(),
      custom_category:          customCategory,
      performed_price_centavos: customPrice,
      item_discount_centavos:   0,
      qty:                      1,
      tooth_number:             "",
      outcome:                  "done",
    });
    setCustomName("");
    setCustomPrice(0);
    setCustomCategory("");
    setCustomOpen(false);
  }

  const customReady = !!customName.trim() && !!customCategory;

  return (
    <div
      className={bare
        ? "space-y-3"
        : emphasis
          ? "bg-white rounded-2xl shadow-sm ring-1 ring-teal-200 p-5 space-y-3"
          : "bg-white rounded-2xl shadow-sm p-4 space-y-3"}
      data-testid={emphasis ? "checkout-encode-visit" : undefined}
    >
      {emphasis && (heading || subheading) ? (
        <div className="space-y-0.5">
          {heading ? <p className="text-sm font-semibold text-slate-900">{heading}</p> : null}
          {subheading ? <p className="text-[12px] text-slate-500">{subheading}</p> : null}
        </div>
      ) : null}
      <div className={rowCls}>
        <button onClick={() => { setSearchOpen(!searchOpen); setCustomOpen(false); }}
          data-testid="checkout-add-procedure"
          data-tutorial-id="checkout-add-procedure"
          className={`inline-flex items-center gap-1.5 font-semibold ${sizeCls} ${addCls} ${btnWidthCls}`}>
          <Plus className="w-3.5 h-3.5" />Add procedure
        </button>
        <button onClick={() => { setCustomOpen(!customOpen); setSearchOpen(false); }}
          data-testid="checkout-add-custom"
          data-tutorial-id="checkout-add-custom"
          className={`inline-flex items-center gap-1.5 font-semibold ${sizeCls} ${customCls} ${btnWidthCls}`}>
          <Plus className="w-3.5 h-3.5" />{customLabel}
        </button>
      </div>

      {showOrthoChargePrompt ? (
        <div
          className="rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2 text-xs text-teal-900"
          data-testid="checkout-ortho-charge-prompt"
        >
          <strong>Confirm with the doctor:</strong> no adjustment today. Add a custom charge only if the repair or extra work is chargeable.
        </div>
      ) : null}

      {searchOpen ? (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="checkout-procedure-picker" data-tutorial-id="checkout-procedure-picker">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input autoFocus type="search" placeholder="Search procedures..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="checkout-procedure-search"
              data-tutorial-id="checkout-procedure-search"
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          </div>
          {loadingSearch ? (
            <p className="text-xs text-slate-400 text-center py-3"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-3">No matches.</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button onClick={() => pickProcedure(p)}
                    data-testid={`procedure-pick-${p.id}`}
                    data-tutorial-id={`procedure-pick-${safeTestId(p.id)}`}
                    className="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{p.name}</p>
                      {p.code ? <p className="text-[11px] text-slate-400">{p.code}</p> : null}
                    </div>
                    <span className="text-xs text-slate-500 font-mono">{fmtPeso(p.default_fee_centavos, patientCurrency)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {customOpen ? (
        <div className="border border-slate-200 rounded-lg p-3 grid sm:grid-cols-[1fr_180px_auto_auto] gap-2 items-end" data-testid="checkout-custom-form">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="custom-name">Name</label>
            <input id="custom-name" value={customName} onChange={(e) => setCustomName(e.target.value)}
              placeholder="e.g. Out-of-pocket consult" autoFocus
              data-testid="checkout-custom-name"
              className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="checkout-custom-category">Category</label>
            <select
              id="checkout-custom-category"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              data-testid="checkout-custom-category"
              className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 bg-white focus:border-teal-500 outline-none"
            >
              <option value="">Pick category...</option>
              {APPROVED_PROCEDURE_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            {!customCategory ? (
              <p data-testid="checkout-custom-category-message" className="mt-1 text-[11px] text-rose-600">
                Pick a category so reports and commission can classify this charge.
              </p>
            ) : null}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Price</label>
            <PesoInput centavos={customPrice} onChange={setCustomPrice} testid="checkout-custom-price" />
          </div>
          <button onClick={addCustom} disabled={!customReady}
            data-testid="checkout-custom-add"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
            <Check className="w-3.5 h-3.5" />Add
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PesoInput({ centavos, onChange, disabled, testid, tutorialId }) {
  const initial = centavosToPesoInput(centavos);
  const [draft, setDraft] = useState(initial);

  useEffect(() => {
    setDraft(centavosToPesoInput(centavos));
  }, [centavos]);

  function onChangeRaw(e) {
    let v = e.target.value.replace(/[^\d.]/g, "");
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
    }
    setDraft(v);
  }

  function commit() {
    const cents = parsePesoToCentavos(draft);
    onChange(cents);
    setDraft(centavosToPesoInput(cents));
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-xs text-slate-400">₱</span>
      <input type="text" inputMode="decimal" value={draft}
        disabled={disabled}
        onChange={onChangeRaw}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
        data-testid={testid}
        data-tutorial-id={tutorialId}
        className="w-24 px-1 py-1 text-right text-sm rounded border border-slate-200 focus:border-teal-500 outline-none disabled:bg-slate-50 disabled:text-slate-400" />
    </div>
  );
}

function fmtPeso(centavos, currency = "PHP") {
  if (centavos == null) return "Depends on case";
  const n = (centavos / 100).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  return currency === "PHP" ? `₱${n}` : `${n} ${currency}`;
}

function safeTestId(value) {
  return String(value || "unknown").replace(/[^a-zA-Z0-9_-]/g, "-");
}

function makeLocalId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
